/**
 * Builder Read Slide Tool Schema
 * 
 * Schema for reading slides from the working presentation file during builds.
 * Supports two types of slides:
 * - workingSlides: Slides being built (content slides)
 * - styleGuideSlides: Hidden reference slides (style guide)
 * 
 * Uses 1-based indexing (slide 1, 2, 3...) to match PowerPoint UI.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// DETAIL LEVEL (matching orchestrator's readSlide)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detail level for slide content retrieval
 * - layout: Fast structural scan (text, positions, basic structure)
 * - complete: Full details including all properties
 */
export const BuilderDetailLevelSchema = z.enum(["layout", "complete"]);
export type BuilderDetailLevel = z.infer<typeof BuilderDetailLevelSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Request for working slides (content slides being built)
 */
export const WorkingSlidesRequestSchema = z.object({
    /** 1-based slide indices to read (e.g., [1, 2, 3]) */
    indexes: z.array(z.number().int().positive())
        .min(1, "At least one slide index required")
        .describe("1-based slide indices to read (e.g., [1, 2, 3])"),
    
    /** Level of detail to return */
    detailLevel: BuilderDetailLevelSchema
        .default("complete")
        .describe('Level of detail: "layout" for basic structure or "complete" for all properties'),
    
    /** If true, include screenshot images of the slides */
    includeImages: z.boolean()
        .optional()
        .default(true)
        .describe("If true, include screenshot images of the slides"),
});

/**
 * Request for style guide slides (hidden reference slides)
 */
export const StyleGuideSlidesRequestSchema = z.object({
    /** 1-based slide indices to read (e.g., [1, 2, 3]) */
    indexes: z.array(z.number().int().positive())
        .min(1, "At least one slide index required")
        .describe("1-based slide indices to read (e.g., [1, 2, 3])"),
    
    /** Level of detail to return */
    detailLevel: BuilderDetailLevelSchema
        .default("complete")
        .describe('Level of detail: "layout" for basic structure or "complete" for all properties'),
    
    /** If true, include screenshot images of the slides */
    includeImages: z.boolean()
        .optional()
        .default(true)
        .describe("If true, include screenshot images of the slides"),
});

// ═══════════════════════════════════════════════════════════════════════════
// TOOL PARAMS
// ═══════════════════════════════════════════════════════════════════════════

export const BuilderReadSlideParamsSchema = z.object({
    /** Request for working slides (content slides being built) */
    workingSlides: WorkingSlidesRequestSchema.optional()
        .describe("Request for working slides (content slides being built)"),
    
    /** Request for style guide slides (hidden reference slides) */
    styleGuideSlides: StyleGuideSlidesRequestSchema.optional()
        .describe("Request for style guide slides (hidden reference slides for styling)"),
}).refine(
    (data) => data.workingSlides !== undefined || data.styleGuideSlides !== undefined,
    { message: "At least one of workingSlides or styleGuideSlides must be provided" }
);

export type BuilderReadSlideParams = z.infer<typeof BuilderReadSlideParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Shape content (simplified from orchestrator's full schema)
 * 
 * Uses Record<string, unknown> for complex nested structures since the backend
 * may return varying shapes of data depending on shape type and detail level.
 */
export interface BuilderShapeContent {
    shapeId: string;
    name?: string;
    type: string;
    position: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
    text?: string;
    paragraphs?: Array<{
        text: string;
        level: number;
    }>;
    // Styled/complete fields - flexible to accommodate varying backend responses
    fill?: Record<string, unknown>;
    outline?: Record<string, unknown>;
    // Table info - flexible structure from backend
    tableInfo?: Record<string, unknown>;
    // Chart info
    chartInfo?: Record<string, unknown>;
    // Carousel metadata - flexible structure from backend
    carouselMetadata?: Record<string, unknown>;
}

/**
 * Single slide result
 * 
 * NOTE: Screenshots are NOT included in the result - they're stored in contextResolver
 * and returned via _imageIds for automatic injection by MessageBuilder.
 */
export interface BuilderSlideResult {
    /** 1-based slide index */
    slideIndex: number;
    /** Layout name if available */
    layoutName?: string;
    /** Whether this is a hidden slide */
    isHidden: boolean;
    /** Custom tags on the slide */
    customTags?: Record<string, string>;
    /** Shapes on the slide */
    shapes: BuilderShapeContent[];
    /** Error if this specific slide failed */
    error?: string;
}

/**
 * Tool response
 */
export interface BuilderReadSlideResponse {
    success: boolean;
    /** Results for working slides */
    workingSlides?: BuilderSlideResult[];
    /** Results for style guide slides */
    styleGuideSlides?: BuilderSlideResult[];
    /** Image IDs for automatic injection */
    _imageIds?: string[];
    /** General error */
    error?: string;
}

export const BuilderReadSlideResponseSchema = z.object({
    success: z.boolean(),
    workingSlides: z.array(z.any()).optional(),
    styleGuideSlides: z.array(z.any()).optional(),
    _imageIds: z.array(z.string()).optional(),
    error: z.string().optional(),
});
